/** QL-375 / QL-664 / QL-677 — the deviation and missing-mandate cards: the options, hints and decisions. Read with `useT(T)`. */
export const T = {
  en_GB: {
    accept: "Accept deviation",
    acceptHintBox:
      "You can proceed the advice. A note is required and is printed in the advice document.",
    acceptedBy: (p: { name: string; time: string }) => `Accepted by ${p.name} · ${p.time}`,
    basisActiveHint:
      "The mandate follows this portfolio: every change to it updates the mandate. Select a template to stop.",
    basisPausedHint: (product: string) =>
      `Not updated: ${product} has no ISIN, so the mandate keeps its last allocation until it is removed or sold in full.`,
    block: "Block",
    blockHint: "The advice stays blocked until the goal's portfolios are adjusted.",
    blockHintMissing: "The advice stays blocked until there is a mandate.",
    blockedBy: (p: { name: string; time: string }) => `Blocked by ${p.name} · ${p.time}`,
    clearDecision: "Clear decision",
    createApplied: "Mandate created from the portfolio",
    createUnavailableNoProducts: "Available once the portfolio holds a product.",
    decisionCleared: "Decision cleared",
    decisionSaved: "Decision saved",
    deviationSubtitle:
      "There is a deviation identified between the mandate and the portfolio. Select your action.",
    deviationTitle: "Mandate deviation",
    error: "Could not save the decision",
    missingSubtitle: "There is no mandate identified. Select your action.",
    missingTitle: "Missing mandate",
    noteLabel: "Note",
    notePlaceholder: "Why the deviation is acceptable for this client",
    optCreateFromPortfolio: "Create mandate from this portfolio",
    optCreateFromPortfolioHint:
      "The mandate is created in the flow and checks are done towards that mandate",
    optCreateFromPortfolioHintCustom:
      "The mandate is updated in the flow and checks are done towards that mandate",
    optCreateFromPortfolioHintExisting:
      "A new mandate is created from this portfolio and replaces the client's existing mandate at the bank; checks are done towards the new mandate",
    optUpdateFromGoal: "Update mandate based on the goals aggregated 1 year portfolio",
    optUpdateFromGoalHint:
      "The mandate is updated in the flow and checks are done towards that mandate",
    save: "Save",
    updateApplied: "Mandate updated from the portfolio",
    updateFromGoalApplied: "Mandate updated from the goal's portfolios",
    updateError: "Could not update the mandate",
    updateFromGoalUnavailableNoBasis:
      "Unavailable until the goal's portfolios have a composition to measure.",
    updateFromGoalUnavailableNoIsin:
      "Unavailable: a holding in the goal has no ISIN, and a mandate names instruments by ISIN.",
    updateFromGoalUnavailableNoWeights:
      "Unavailable until the goal's portfolios have amounts — they all weigh 0 % now.",
    updateUnavailableNoIsin:
      "Unavailable: a holding in this portfolio has no ISIN or cannot be matched to a product, and a mandate names instruments by ISIN. Remove it or sell it in full.",
    updateUnavailableNoProducts: "Update is unavailable until the portfolio holds a product.",
    updateUnavailableNoWeights:
      "Update is unavailable until the portfolio's products have amounts — they all weigh 0 % now.",
    updateUnavailableNotCustom: "Update is available on custom portfolios only.",
  },
  nb_NO: {
    accept: "Aksepter avvik",
    acceptHintBox:
      "Du kan gå videre med rådet. En merknad er påkrevd og skrives ut i rådgivningsdokumentet.",
    acceptedBy: (p: { name: string; time: string }) => `Akseptert av ${p.name} · ${p.time}`,
    basisActiveHint:
      "Mandatet følger denne porteføljen: hver endring i den oppdaterer mandatet. Velg en mal for å stoppe.",
    basisPausedHint: (product: string) =>
      `Ikke oppdatert: ${product} har ingen ISIN, så mandatet beholder sin siste fordeling til produktet fjernes eller selges helt.`,
    block: "Blokker",
    blockHint: "Rådgivningen forblir blokkert til målets porteføljer er justert.",
    blockHintMissing: "Rådet forblir blokkert til det finnes et mandat.",
    blockedBy: (p: { name: string; time: string }) => `Blokkert av ${p.name} · ${p.time}`,
    clearDecision: "Fjern beslutning",
    createApplied: "Mandat opprettet fra porteføljen",
    createUnavailableNoProducts: "Tilgjengelig når porteføljen inneholder et produkt.",
    decisionCleared: "Beslutning fjernet",
    decisionSaved: "Beslutning lagret",
    deviationSubtitle:
      "Det er funnet et avvik mellom mandatet og porteføljen. Velg hva du vil gjøre.",
    deviationTitle: "Mandatavvik",
    error: "Kunne ikke lagre beslutningen",
    missingSubtitle: "Ingen mandat er identifisert. Velg hva du vil gjøre.",
    missingTitle: "Mangler mandat",
    noteLabel: "Begrunnelse",
    notePlaceholder: "Hvorfor avviket er akseptabelt for denne kunden",
    optCreateFromPortfolio: "Opprett mandat fra denne porteføljen",
    optCreateFromPortfolioHint:
      "Mandatet opprettes i flyten, og kontrollene gjøres mot dette mandatet",
    optCreateFromPortfolioHintCustom:
      "Mandatet oppdateres i flyten, og kontrollene gjøres mot dette mandatet",
    optCreateFromPortfolioHintExisting:
      "Et nytt mandat opprettes fra denne porteføljen og erstatter kundens eksisterende mandat hos banken; kontrollene gjøres mot det nye mandatet",
    optUpdateFromGoal: "Oppdater mandatet basert på målets samlede ett-års-portefølje",
    optUpdateFromGoalHint: "Mandatet oppdateres i flyten, og kontrollene gjøres mot det mandatet",
    save: "Lagre",
    updateApplied: "Mandatet er oppdatert fra porteføljen",
    updateFromGoalApplied: "Mandatet er oppdatert fra målets porteføljer",
    updateError: "Kunne ikke oppdatere mandatet",
    updateFromGoalUnavailableNoBasis:
      "Utilgjengelig til målets porteføljer har en sammensetning å måle.",
    updateFromGoalUnavailableNoIsin:
      "Utilgjengelig: en beholdning i målet mangler ISIN, og et mandat navngir instrumenter med ISIN.",
    updateFromGoalUnavailableNoWeights:
      "Utilgjengelig til målets porteføljer har beløp — alle veier 0 % nå.",
    updateUnavailableNoIsin:
      "Ikke tilgjengelig: en beholdning i porteføljen mangler ISIN eller kan ikke knyttes til et produkt, og et mandat navngir instrumenter med ISIN. Fjern den eller selg den helt.",
    updateUnavailableNoProducts:
      "Oppdatering er ikke tilgjengelig før porteføljen inneholder et produkt.",
    updateUnavailableNoWeights:
      "Oppdatering er ikke tilgjengelig før porteføljens produkter har beløp — alle veier 0 % nå.",
    updateUnavailableNotCustom: "Oppdatering er bare tilgjengelig på egendefinerte porteføljer.",
  },
} as const;
